import React, { Suspense } from 'react';
import { memberId } from '@trello/session-cookie';
import { useLazyComponent } from '@trello/use-lazy-component';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { useMigrationWizardBanner } from 'app/src/components/MigrationWizard/useMigrationWizardBanner';

export const Banners = () => {
  const AtlassianAccountMigrationStageBanners = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "atlassian-account-migration-stage-banners" */ 'app/src/components/AtlassianAccountMigrationStage'
      ),
    { namedImport: 'AtlassianAccountMigrationStageBanners', preload: false },
  );

  const BoardBannerList = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "board-banner-list" */ 'app/src/components/BoardBannerList'
      ),
    { namedImport: 'BoardBannerList', preload: false },
  );

  const MigrationWizardBanner = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "migration-wizard" */ 'app/src/components/MigrationWizard'
      ),
    { namedImport: 'MigrationWizard' },
  );

  const SomethingsWrongBanner = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "somethings-wrong-banner" */ 'app/src/components/SomethingsWrongBanner'
      ),
    { namedImport: 'SomethingsWrongBanner', preload: false },
  );

  const wouldRenderSomethingsWrongBanner = useFeatureFlag(
    'fep.show_somethings_wrong',
    false,
  );

  const {
    wouldRender: wouldRenderMigrationWizardBanner,
  } = useMigrationWizardBanner({ skip: wouldRenderSomethingsWrongBanner });

  // Don't render any of the banners if the user isn't logged in
  if (!memberId) {
    return null;
  }

  let banner;

  // Chain conditional statements in order of banner priority
  if (wouldRenderSomethingsWrongBanner) {
    banner = (
      <Suspense fallback={null}>
        <ChunkLoadErrorBoundary fallback={null}>
          <SomethingsWrongBanner />
        </ChunkLoadErrorBoundary>
      </Suspense>
    );
  } else if (wouldRenderMigrationWizardBanner) {
    banner = (
      <Suspense fallback={null}>
        <ChunkLoadErrorBoundary fallback={null}>
          <MigrationWizardBanner />
        </ChunkLoadErrorBoundary>
      </Suspense>
    );
  } else {
    // TODO: Lift business logic from each banner to help avoid performance problems.
    // See: https://hello.atlassian.net/wiki/spaces/TRELLOFE/pages/1221169471/Building+and+managing+targeted+experiences
    banner = (
      <Suspense fallback={null}>
        <ChunkLoadErrorBoundary fallback={null}>
          <BoardBannerList />
        </ChunkLoadErrorBoundary>
      </Suspense>
    );
  }

  // AtlassianAccountMigrationStageBanners doesn't technically render a banner
  // anymore. But it does render dialogs that are critical for users to see to
  // finish migrating their account to Aa. The only time we don't want to render
  // these dialogs is when there's an incident and the <SomethingWrongBanner>
  // should be shown.
  const aaMigrationBanner = wouldRenderSomethingsWrongBanner ? null : (
    <Suspense fallback={null}>
      <ChunkLoadErrorBoundary fallback={null}>
        <AtlassianAccountMigrationStageBanners />
      </ChunkLoadErrorBoundary>
    </Suspense>
  );

  return (
    <ComponentWrapper>
      {banner}
      {aaMigrationBanner}
    </ComponentWrapper>
  );
};
